import { RendererType } from './renderer-type';
import { FormatterFn, FormatterService, StandardFormatterId } from '../../util/formatter-service';
import { FieldDefinition, FieldPrimitiveType, isAllowedFormat } from './field-definition.schema';

const DEFAULT_RENDERER_BY_TYPE: Record<FieldPrimitiveType, RendererType> = {
  number: RendererType.number,
  string: RendererType.text,
  boolean: RendererType.boolean,
  date: RendererType.date,
  array: RendererType.multiSelect,
};

export function getRendererType(field: FieldDefinition): RendererType {
  if (field.format !== undefined && isAllowedFormat(field.type, field.format)) {
    return field.format;
  }
  return DEFAULT_RENDERER_BY_TYPE[field.type];
}

/**
 * Bridges `RendererType` values to the formatter-service's `StandardFormatterId`.
 * Most `RendererType` values double as formatter ids; the deltas live here so
 * call sites can ask for "the formatter id for this field" without re-deriving
 * the rule. Currently `number` is the only renderer that maps to a different
 * formatter id (`'numeric'`) — the legacy `ColumnFormatter` carried this same
 * translation under `deprecatedFormattersToNew`.
 */
const RENDERER_TO_FORMATTER_ID: Partial<Record<RendererType, StandardFormatterId>> = {
  [RendererType.number]: 'numeric',
};

/**
 * Resolves the `StandardFormatterId` for a `FieldDefinition`. Returns the
 * derived renderer type when no override applies. Type-cast through the
 * renderer enum because most renderer ids are also valid formatter ids;
 * unrecognized ids fall through to `FormatterService.getFormatterForId`'s
 * default formatter, matching the pre-migration behavior.
 */
export function getFormatterId(field: FieldDefinition): StandardFormatterId {
  const renderer = getRendererType(field);
  return RENDERER_TO_FORMATTER_ID[renderer] ?? (renderer as StandardFormatterId);
}

/**
 * Resolves a formatter for a `FieldDefinition`. Routes through
 * `FormatterService.getFormatterForId` keyed by `getFormatterId(field)` so
 * the renderer-id → formatter-id translation lives in one place.
 */
export function getFormatter<T = unknown>(field: FieldDefinition): FormatterFn<T> {
  return FormatterService.get().getFormatterForId<T>(getFormatterId(field));
}

export interface FormMeta {
  rendererType: RendererType;
  defaultValue?: unknown;
  enumOptions?: { value: string; label: string; group?: string }[];
}

export interface GridMeta {
  rendererType: RendererType;
  aggFunc?: string;
  filterType: 'text' | 'number' | 'date' | 'set' | 'boolean';
  groupable: boolean;
  sortable: boolean;
}

const FILTER_TYPE_BY_PRIMITIVE: Record<FieldPrimitiveType, GridMeta['filterType']> = {
  number: 'number',
  string: 'text',
  boolean: 'boolean',
  date: 'date',
  array: 'set',
};

export function getFormMeta(field: FieldDefinition): FormMeta {
  return {
    rendererType: getRendererType(field),
    enumOptions: field.enumOptions?.map(({ value, label, group }) => ({ value, label, group })),
  };
}

export function getGridMeta(field: FieldDefinition): GridMeta {
  return {
    rendererType: getRendererType(field),
    filterType: FILTER_TYPE_BY_PRIMITIVE[field.type],
    groupable: field.type !== FieldPrimitiveType.array,
    sortable: true,
  };
}
